// TECH DEBT: tokens stored in localStorage are accessible to any JS running on the page (XSS risk).
// Migrate to httpOnly cookies managed by the backend so tokens are invisible to JS.
// Tracked: requires backend changes to set/clear cookies on login/logout/refresh endpoints.
class TokenService {
  private readonly ACCESS_TOKEN_KEY = "auth:accessToken";
  private readonly TOKEN_EXPIRY_KEY = "auth:tokenExpiry";
  private readonly REFRESH_BUFFER_SECONDS = 120; // 2 minutes before expiry

  setTokens(token: string, expiresAt?: number): void {
    const expiryTime = expiresAt || Date.now() + 30 * 60 * 1000; // 30min default
    
    localStorage.setItem(this.ACCESS_TOKEN_KEY, token);
    localStorage.setItem(this.TOKEN_EXPIRY_KEY, expiryTime.toString());
  }

  getAccessToken(): string | null {
    return localStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  getTokenExpiry(): number {
    const expiry = localStorage.getItem(this.TOKEN_EXPIRY_KEY);
    return expiry ? parseInt(expiry, 10) : 0;
  }

  isTokenExpired(): boolean {
    const expiry = this.getTokenExpiry();
    if (expiry === 0) return true;
    return Date.now() >= expiry;
  }

  isTokenExpiringSoon(seconds: number = this.REFRESH_BUFFER_SECONDS): boolean {
    const expiry = this.getTokenExpiry();
    if (expiry === 0) return true;
    return expiry - Date.now() <= seconds * 1000;
  }

  getTokenTimeLeft(): number {
    const expiry = this.getTokenExpiry();
    if (expiry === 0) return 0;
    return Math.max(0, Math.floor((expiry - Date.now()) / 1000));
  }

  clearTokens(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.TOKEN_EXPIRY_KEY);
  }

  hasValidToken(): boolean {
    const token = this.getAccessToken();
    return !!token && !this.isTokenExpired();
  }
}

export default new TokenService();